// src/components/EquipmentManager/EquipmentManager.js

import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import EquipmentList from './EquipmentList';
import AddEquipmentModal from './AddEquipmentModal';
import AssignEquipmentModal from './AssignEquipmentModal';
import EquipmentDetailsModal from './EquipmentDetailsModal';
import StatsBar from './StatsBar';
import EquipmentFilters from './EquipmentFilters';
import EquipmentAssignmentsList from './EquipmentAssignmentsList';
import MyAssignmentsView from './MyAssignmentsView';
import ErrorBoundary from '../common/ErrorBoundary';
import { SkeletonStats } from '../common/LoadingSkeleton';
import styles from './EquipmentManager.module.css';
import { FaPlus, FaBoxOpen, FaCog, FaSearch, FaBox, FaClipboardList } from 'react-icons/fa';
import { useUser } from '../../context/UserContext';
import { canManageProgram } from '../../hooks/useProgramsApi';
import { useNotification } from '../../context/NotificationContext';
import useEquipment from '../../hooks/useEquipment';
import usePolling from '../../hooks/usePolling';
import equipmentApi from '../../api/equipmentApi';


const EquipmentManager = () => {
  const { programId } = useParams();
  const { userClubs, userProfile } = useUser();
  const { showSuccess, showError } = useNotification();
  
  const [program, setProgram] = useState(null);
  const [team, setTeam] = useState(null);
  
  const [loadingProgram, setLoadingProgram] = useState(true);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [errorProgram, setErrorProgram] = useState(null);
  const [errorTeam, setErrorTeam] = useState(null);

  // Permission state
  const [programPermissions, setProgramPermissions] = useState([]);
  const [resolvedClubId, setResolvedClubId] = useState(null);
  
  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    minRating: '',
    sponsor: '',
    sort: 'newest'
  });
  
  // Assignments for stats calculation
  const [assignments, setAssignments] = useState([]);
  
  // Categories from backend
  const [categories, setCategories] = useState([]);
  
  // View state: 'equipment' or 'assignments'
  const [currentView, setCurrentView] = useState('equipment');

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [equipmentToAssign, setEquipmentToAssign] = useState(null);
  
  // Edit Assignment Modal State
  const [isEditAssignmentModalOpen, setIsEditAssignmentModalOpen] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);

  // Use equipment hook with filters
  const { 
    equipment, 
    loading: loadingEquipment, 
    error: errorEquipment,
    availableSponsors,
    refresh: refreshEquipmentData 
  } = useEquipment(programId, filters);
  
  // Poll for updates every 30 seconds
  usePolling(refreshEquipmentData, 30000, true);
  
  // Authorization logic (following TeamItemsPage pattern)
  const canManage = useMemo(() => {
    // Check if user has club management role
    const hasClubRole = Array.isArray(userClubs) && userClubs.some((m) => {
      const membershipClubId = m.club?._id || m.club;
      const role = (m.role || '').toLowerCase();
      const isTargetClub = String(membershipClubId) === String(resolvedClubId);
      const isOwnerOrManager = ['owner', 'creator', 'clubowner', 'clubmanager', 'manager', 'admin'].includes(role);
      return isTargetClub && isOwnerOrManager;
    });

    // Check if user has program management permissions
    const hasProgramPermissions = canManageProgram(programPermissions);

    return hasClubRole || hasProgramPermissions;
  }, [userClubs, resolvedClubId, programPermissions]);
  
  // Fetch program details to get clubId
  useEffect(() => {
    if (programId) {
      const fetchProgramDetails = async () => {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}`
          );
          const prog = response.data;
          
          // Extract clubId correctly
          const extractedClubId =
            prog.clubId && typeof prog.clubId === 'object'
              ? prog.clubId._id
              : prog.clubId;
              
          setResolvedClubId(extractedClubId);
          setProgram(prog);
          setLoadingProgram(false);
        } catch (err) {
          console.error('Error fetching program details:', err);
          setErrorProgram(err.message);
          setLoadingProgram(false);
        }
      };
      
      fetchProgramDetails();
    }
  }, [programId]);
  
  // Fetch program permissions
  useEffect(() => {
    if (!programId) return;
    
    const fetchProgramPermissions = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}/permissions`
        );
        if (response.ok) {
          const data = await response.json();
          setProgramPermissions(data?.permissions || []);
        } else {
          setProgramPermissions([]);
        }
      } catch (error) {
        console.error('Error fetching program permissions:', error);
        setProgramPermissions([]);
      }
    };

    fetchProgramPermissions();
  }, [programId]);
  
  // Fetch categories for filtering
  useEffect(() => {
    const fetchCategories = async () => {
      if (!resolvedClubId) return;
      
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/equipment-categories?clubId=${resolvedClubId}`
        );
        
        if (response.ok) {
          const data = await response.json();
          const categoriesArray = data.categories || data || [];
          setCategories(categoriesArray);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    
    fetchCategories();
  }, [resolvedClubId]);

  // Fetch Team Data based on Program ID (legacy, not critical)
  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/teams/${programId}`
        );
        if (!response.ok) {
          // Team endpoint not critical - just set empty team
          setTeam({});
          setLoadingTeam(false);
          return;
        }
        const data = await response.json();
        setTeam(data);
      } catch (error) {
        console.warn('Team fetch failed (not critical):', error);
        setTeam({}); // Set empty object instead of error
        // Don't set error - team is not critical for equipment page
      } finally {
        setLoadingTeam(false);
      }
    };

    if (programId) {
      fetchTeam();
    } else {
      setLoadingTeam(false);
    }
  }, [programId]);
  
  // Fetch assignments for stats calculation
  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/equipment-assignments/program/${programId}`
        );
        if (response.ok) {
          const data = await response.json();
          // Handle both array and object responses
          const assignmentsArray = Array.isArray(data) ? data : (data.assignments || []);
          setAssignments(assignmentsArray);
        }
      } catch (error) {
        console.error('Error fetching assignments:', error);
        setAssignments([]); // Set empty array on error
      }
    };

    if (programId) {
      fetchAssignments();
    }
  }, [programId]);
  
  // Calculate stats
  const stats = useMemo(() => {
    return equipmentApi.calculateStats(equipment, assignments);
  }, [equipment, assignments]);

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };
  
  // Handle equipment refresh
  const handleEquipmentAdded = () => {
    refreshEquipmentData();
    showSuccess('Equipment added successfully!');
  };
  
  const handleEquipmentUpdated = () => {
    refreshEquipmentData();
    closeEditModal();
    showSuccess('Equipment updated successfully!');
  };
  
  const handleEquipmentDeleted = () => {
    refreshEquipmentData();
  };

  // Handle Refresh Assignments
  const handleAssignmentUpdated = () => {
    // Refresh assignments data
    const fetchAssignments = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/equipment-assignments/program/${programId}`
        );
        if (response.ok) {
          const data = await response.json();
          // Handle both array and object responses
          const assignmentsArray = Array.isArray(data) ? data : (data.assignments || []);
          setAssignments(assignmentsArray);
        }
      } catch (error) {
        console.error('Error fetching assignments:', error);
        setAssignments([]); // Set empty array on error
      }
    };
    fetchAssignments();
  };

  // Edit Assignment Modal Controls
  const openEditAssignmentModal = (assignment) => {
    setEditingAssignment(assignment);
    setIsEditAssignmentModalOpen(true);
  };

  const closeEditAssignmentModal = () => {
    setIsEditAssignmentModalOpen(false);
    setEditingAssignment(null);
  };

  const handleAssignmentEditUpdated = (updatedAssignment) => {
    handleAssignmentUpdated();
    closeEditAssignmentModal();
  };

  // Modal Controls
  const openAddModal = () => {
    if (!program || !program.sport) {
      showError('Cannot add equipment: program data not loaded');
      return;
    }
    setIsAddModalOpen(true);
  };

  const closeAddModal = () => {
    setIsAddModalOpen(false);
  };

  const openAssignModal = (equipment = null) => {
    setEquipmentToAssign(equipment);
    setIsAssignModalOpen(true);
  };

  const closeAssignModal = () => {
    setIsAssignModalOpen(false);
    setEquipmentToAssign(null);
  };

  // Edit Equipment Modal Controls
  const openEditModal = (equipment) => {
    setEditingEquipment(equipment);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditingEquipment(null);
    setIsEditModalOpen(false);
  };

  // Equipment Details Modal Controls
  const openEquipmentDetails = async (equipment) => {
    setSelectedEquipment(equipment);
    // Track view
    await equipmentApi.trackView(equipment._id);
  };

  const closeEquipmentDetails = () => {
    setSelectedEquipment(null);
  };

  // Combined Loading State
  const loading = loadingProgram || loadingTeam;
  const error = errorProgram || errorTeam;

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.mainContent}>
          <div className={styles.compactHeader}>
            <div className={styles.headerLeft}>
              <FaCog className={styles.headerIcon} />
              <h1 className={styles.headerTitle}>Equipment Store</h1>
              <span className={styles.itemCount}>Loading...</span>
            </div>
          </div>
          <SkeletonStats />
          <div className={styles.message}>Loading program details...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.mainContent}>
          <div className={styles.message}>Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary errorTitle="Equipment Manager Error" onReset={() => window.location.reload()}>
      <div className={styles.container}>
        <div className={styles.mainContent}>
          {/* Compact Sticky Header with Tabs */}
          <div className={styles.compactHeader}>
            {/* Left Section - Title */}
            <div className={styles.headerLeft}>
              <FaCog className={styles.headerIcon} />
              <h1 className={styles.headerTitle}>Equipment Store</h1>
              <span className={styles.itemCount}>{equipment.length} items</span>
              {program && program.sport && (
                <span className={styles.sportBadge}>{program.sport}</span>
              )}
            </div>
            
            {/* Center Section - Tabs */}
            <div className={styles.compactTabs}>
              <button
                className={`${styles.compactTabButton} ${currentView === 'equipment' ? styles.activeTab : ''}`}
                onClick={() => setCurrentView('equipment')}
              >
                <span className={styles.tabIcon}>📦</span>
                <span className={styles.tabText}>Equipment</span>
                <span className={styles.tabCount}>({equipment.length})</span>
              </button>
              <button
                className={`${styles.compactTabButton} ${currentView === 'assignments' ? styles.activeTab : ''}`}
                onClick={() => setCurrentView('assignments')}
              >
                <span className={styles.tabIcon}>📋</span>
                <span className={styles.tabText}>My Assignments</span>
                {stats.overdueCount > 0 && (
                  <span className={styles.badge}>{stats.overdueCount}</span>
                )}
              </button>
            </div>
            
            {/* Right Section - Quick Stats */}
            <div className={styles.headerStats}>
              <span className={styles.statPill}>⭐ {stats.averageRating?.toFixed(1) || 'N/A'}</span>
              {stats.overdueCount > 0 && (
                <span className={`${styles.statPill} ${styles.warning}`}>⚠ {stats.overdueCount} Overdue</span>
              )}
            </div>
          </div>
          
          {/* Show filters only in equipment view */}
          {currentView === 'equipment' && (
            <div className={styles.controlsBar}>
            {/* Compact Stats for smaller screens */}
            <div className={styles.inlineStats}>
              <span className={styles.stat}>✓ {stats.totalAssigned || 0} Assigned</span>
            </div>
            
            {/* Search & Filters in One Row */}
            <div className={styles.searchBar}>
              <FaSearch className={styles.searchIcon} />
              <input 
                type="text"
                placeholder="Search equipment..."
                value={filters.search}
                onChange={(e) => handleFilterChange({ ...filters, search: e.target.value })}
                className={styles.searchInput}
              />
            </div>
            
            <select 
              value={filters.category} 
              onChange={(e) => handleFilterChange({ ...filters, category: e.target.value })}
              className={styles.filterSelect}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat._id} value={cat._id}>
                  {cat.icon} {cat.name}
                </option>
              ))}
            </select>
            
            <select 
              value={filters.minRating} 
              onChange={(e) => handleFilterChange({ ...filters, minRating: e.target.value })}
              className={styles.filterSelect}
            >
              <option value="">All Ratings</option>
              <option value="4">⭐ 4+</option>
              <option value="3">⭐ 3+</option>
            </select>
            
            <select 
              value={filters.sort} 
              onChange={(e) => handleFilterChange({ ...filters, sort: e.target.value })}
              className={styles.filterSelect}
            >
              <option value="newest">Newest</option>
              <option value="rating">Top Rated</option>
              <option value="name">A-Z</option>
            </select>
            </div>
          )}

          {/* Equipment Grid or My Assignments View */}
          {currentView === 'equipment' ? (
            <EquipmentList
              equipments={equipment}
              loading={loadingEquipment}
              error={errorEquipment}
              canManage={canManage}
              programId={programId}
              onEditEquipment={openEditModal}
              onEquipmentClick={openEquipmentDetails}
              onAssign={openAssignModal}
              onEquipmentDeleted={handleEquipmentDeleted}
            />
          ) : (
            <MyAssignmentsView 
              programId={programId} 
              canManage={canManage}
              userId={userProfile?._id}
            />
          )}

          {/* Floating Action Buttons - Bottom Right (Like Teams page) */}
          {canManage && (
            <div className={styles.floatingActions}>
              <button 
                className={styles.floatingAssignButton} 
                onClick={() => openAssignModal()} 
                title="Assign Equipment"
              >
                <FaBoxOpen />
              </button>
              <button 
                className={styles.floatingAddButton} 
                onClick={openAddModal} 
                title="Add Equipment"
              >
                <FaPlus />
              </button>
            </div>
          )}
        </div>
      
        {/* Add Equipment Modal */}
        {isAddModalOpen && program && program.sport && (
          <AddEquipmentModal
            isOpen={isAddModalOpen}
            onClose={closeAddModal}
            onEquipmentAdded={handleEquipmentAdded}
            sportName={program.sport}
            programId={programId}
          />
        )}
        
        {/* Edit Equipment Modal */}
        {isEditModalOpen && editingEquipment && program && program.sport && (
          <AddEquipmentModal
            isOpen={isEditModalOpen}
            onClose={closeEditModal}
            onEquipmentAdded={handleEquipmentUpdated}
            sportName={program.sport}
            programId={programId}
            equipmentToEdit={editingEquipment}
          />
        )}
        
        {/* Assign Equipment Modal */}
        {isAssignModalOpen && (
          <AssignEquipmentModal
            isOpen={isAssignModalOpen}
            onClose={closeAssignModal}
            programId={programId}
            onAssignmentUpdated={handleAssignmentUpdated}
            preSelectedEquipment={equipmentToAssign}
          />
        )}
        
        {/* Equipment Details Modal */}
        {selectedEquipment && (
          <EquipmentDetailsModal
            equipment={selectedEquipment}
            onClose={closeEquipmentDetails}
            programId={programId}
            canManage={canManage}
            onAssign={openAssignModal}
          />
        )}
        
        {/* Edit Assignment Modal */}
        {isEditAssignmentModalOpen && editingAssignment && (
          <AssignEquipmentModal
            isOpen={isEditAssignmentModalOpen}
            onClose={closeEditAssignmentModal}
            programId={programId}
            assignmentToEdit={editingAssignment}
            onAssignmentUpdated={handleAssignmentEditUpdated}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default EquipmentManager;
